<template>
  <div class="pb-8">
    <tml-form ref="quoteForm" form-name="quote-form">
      <div class="form-block row">
        <div class="col-md-8">
          <p class="pb-2">How many items do you need?</p>
          <tml-input
            v-model="quantity"
            type="number"
            field-name="quantity"
            number-iterator
            :step="10"
            :min="0"
            :max="100000"
          />
        </div>
      </div>
      <div class="form-block">
        <p class="pb-2">And what kind of products do you have in mind?</p>
        <div class="row">
          <div class="col-md-8">
            <tml-segmented-select
              v-if="!waitingForProducts"
              v-model="productSku"
              identifier="value"
              :items="products"
              :multiselect="false"
            >
              <template #default="{select, deselect, allItems}">
                <tml-grid
                  class="pods"
                  :min-item-size="300"
                  :breakpoints="{
                    xs: 2,
                    md: 4,
                  }"
                  spacing="1em"
                >
                  <tml-new-pod
                    v-for="product in allItems"
                    :key="product.value"
                    :title="product.text"
                    :image="formatUrl(product.image)"
                    horizontal-alignment="center"
                    :class="{'segmented-active-select': product.selected}"
                    @click="
                      product.selected ? deselect(product) : select(product)
                    "
                  />
                </tml-grid>
              </template>
            </tml-segmented-select>
            <div v-else class="w-full h-full relative">
              <tml-spinner height="60px" />
            </div>
          </div>
        </div>
      </div>

      <div class="form-block row">
        <div class="col-md-8">
          <p class="pb-2">Do you know how many colours are in your design?</p>
          <tml-segmented-control
            v-model="embellishmentSku"
            field-name="embellishment"
            :options="embellishmentList"
            :validation-rules="{required: true}"
          />

          <tml-textarea
            class="mb-6"
            field-name="extraInfo"
            placeholder="Anything you'd like us to know"
          />

          <tml-segmented-control
            v-model="industry"
            field-name="industry"
            label="Organisation type"
            :options="[
              {text: 'Business', value: 'business'},
              {text: 'Charity', value: 'charities'},
              {text: 'Other', value: 'other'},
            ]"
            :validation-rules="{required: true}"
          />
        </div>
      </div>

      <div class="form-block row">
        <div class="col-md-8">
          <p class="pb-2">
            Who should we send the price list and info pack to?
          </p>
          <div class="row">
            <div class="col-md-6">
              <tml-input
                :class="{error}"
                placeholder="First Name"
                type="text"
                field-name="firstName"
                :validation-rules="{required: true}"
              />
            </div>
            <div class="col-md-6">
              <tml-input
                :class="{error}"
                placeholder="Last Name"
                type="text"
                field-name="lastName"
                :validation-rules="{required: true}"
              />
            </div>
          </div>
          <tml-input
            ref="email"
            :class="{error}"
            placeholder="Email"
            type="email"
            field-name="email"
            :validation-rules="{required: true, email: true}"
          />
          <div class="row">
            <div class="col-md-6">
              <tml-input
                ref="company"
                :class="{error}"
                placeholder="Company"
                type="text"
                field-name="company"
              />
            </div>
            <div class="col-md-6">
              <tml-input
                ref="tel"
                :class="{error}"
                placeholder="Phone"
                type="tel"
                field-name="tel"
                :validation-rules="{required: true}"
              />
            </div>
          </div>
        </div>
      </div>
    </tml-form>
    <div class="row">
      <div class="col-md-8">
        <tml-button
          primary
          fill
          :disabled="quoteSubmitting"
          text="Calculate Price"
          href="#"
          :loading="quoteSubmitting"
          @click="onSubmitQuote"
        />
      </div>
    </div>
    <quote-popup
      :product-name="productName"
      :unit-price="unitPrice"
      :quantity="quantity"
    />
    <studio-popup />
  </div>
</template>

<script>
import {useGtm} from '@teemill/common/services';
import {formatUrl} from '@teemill/common/helpers';
import {overlays} from '@teemill/modules/overlays';
import quotePopup from './QuotePopup.vue';
import studioPopup from './StudioPopup.vue';

export default {
  name: 'GetAQuote',
  components: {
    quotePopup,
    studioPopup,
  },
  data() {
    return {
      error: '',
      quoteSubmitting: false,
      products: [],
      waitingForProducts: true,
      productSku: null,
      embellishmentSku: null,
      productName: '',
      unitPrice: 0,
      quantity: 0,
      embellishmentList: [
        {text: '1 Colour', value: 'EMB-04', moq: 50},
        {text: '2 Colours', value: 'EMB-05', moq: 100},
        {text: '3 or more', value: 'EMB-06', moq: 100},
      ],
      industry: null,
    };
  },

  mounted() {
    this.axios
      .get(formatUrl('/omnis/v3/frontend/quote/'))
      .success(data => {
        this.products = data;
      })
      .validation()
      .oops()
      .finally(() => {
        this.waitingForProducts = false;
      });
  },
  methods: {
    onSubmitQuote() {
      if (!this.quoteSubmitting) {
        this.$refs.quoteForm.validateAll().then(valid => {
          if (valid) {
            if (this.quantity < 50) {
              overlays.open('studio-popup');
              return;
            }

            const moq = this.getMoq();
            if (this.quantity < moq) {
              const embellishment = this.embellishmentList.find(
                emb => emb.value === this.embellishmentSku
              );
              if (embellishment) {
                let colourText = '';
                if (embellishment.text === '3 or more') {
                  colourText = '3 or more colours';
                } else {
                  colourText = embellishment.text.toLowerCase();
                }

                this.$dialogs.showSnackBar({
                  message: `Minimum quantity for ${colourText} is ${moq}`,
                  type: 'error',
                });
                return;
              }
            }

            this.quoteSubmitting = true;

            if (!this.productSku) {
              this.quoteSubmitting = false;
              this.$dialogs.showSnackBar({
                type: 'error',
                message: 'Please select a product',
              });
              return;
            }

            const formData = this.$store.getters['forms/quote-form/formData'];

            this.axios
              .post(formatUrl('/omnis/v3/frontend/quote/submit/'), {
                first_name: formData.firstName.value,
                last_name: formData.lastName.value,
                email: formData.email.value,
                phone: formData.tel.value,
                company: formData.company.value,
                quantity: formData.quantity.value,
                product_sku: this.productSku.value,
                embellishment_sku: this.embellishmentSku,
                extra_info: formData.extraInfo.value,
                industry: this.industry,
              })
              .then(({data}) => {
                if (data) {
                  this.setEnhancedConversionsVariables(formData);

                  useGtm().trackEvent({
                    event: 'onSubmitQuote',
                  });

                  this.quoteSubmitting = false;
                  this.productName = data.productName;
                  this.unitPrice = parseFloat(data.unitPrice);
                  this.quantity = parseInt(data.quantity, 10);
                  overlays.open('quote-popup');
                  this.error = null;
                } else {
                  this.quoteSubmitting = false;
                  this.error = data.message;
                }
              })
              .catch(({response}) => {
                this.quoteSubmitting = false;
                if (response) {
                  switch (response.status) {
                    case 422:
                      this.handleServerValidationErrors(response);
                      break;

                    default:
                      this.$dialogs.showSnackBar({
                        type: 'error',
                        message:
                          'Sorry, something went wrong, please refresh the page or try again later',
                      });

                      throw new Error(JSON.stringify(response));
                  }
                }
              });
          }
        });
      }
    },
    /**
     * Get the first error from the server response and show in snackbar
     */
    handleServerValidationErrors({data}) {
      this.$dialogs.showSnackBar({
        type: 'error',
        message: Object.values(data.errors)[0][0],
      });
    },
    getMoq() {
      const embellishment = this.embellishmentList.find(
        emb => emb.value === this.embellishmentSku
      );

      if (embellishment) {
        return embellishment.moq;
      }

      return 100;
    },

    setEnhancedConversionsVariables(formData) {
      window.quoteEmail = formData.email.value;
      window.quotePhoneNumber = formData.tel.value;
    },

    formatUrl,
  },
};
</script>
<style scoped>
.form-block {
  padding-bottom: 1em;
}
.segmented-active-select {
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
}
</style>
